/**
 * Label Store
 * Manages label state using Zustand
 */

import { create } from 'zustand';
import { Label, LabelWithTaskCount, TaskWithLabels } from '@/types';

interface LabelState {
  // State
  labels: Label[];
  labelsWithCounts: LabelWithTaskCount[];
  taskLabels: Map<string, Label[]>;
  isLoading: boolean;
  error: string | null;

  // Actions
  setLabels: (labels: Label[]) => void;
  setLabelsWithCounts: (labels: LabelWithTaskCount[]) => void;
  addLabel: (label: Label) => void;
  updateLabel: (label: Label) => void;
  removeLabel: (labelId: string) => void;
  setTaskLabels: (taskId: string, labels: Label[]) => void;
  setMultipleTaskLabels: (taskLabels: TaskWithLabels[]) => void;
  addLabelToTask: (taskId: string, label: Label) => void;
  removeLabelFromTask: (taskId: string, labelId: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearLabels: () => void;
}

export const useLabelStore = create<LabelState>()((set) => ({
  // Initial state
  labels: [],
  labelsWithCounts: [],
  taskLabels: new Map(),
  isLoading: false,
  error: null,

  // Actions
  setLabels: (labels) => set({ labels }),

  setLabelsWithCounts: (labels) => set({ labelsWithCounts: labels }),

  addLabel: (label) =>
    set((state) => ({
      labels: [...state.labels, label],
    })),

  updateLabel: (label) =>
    set((state) => ({
      labels: state.labels.map((l) =>
        l.labelId === label.labelId ? label : l
      ),
      labelsWithCounts: state.labelsWithCounts.map((l) =>
        l.labelId === label.labelId ? { ...l, ...label } : l
      ),
    })),

  removeLabel: (labelId) =>
    set((state) => ({
      labels: state.labels.filter((l) => l.labelId !== labelId),
      labelsWithCounts: state.labelsWithCounts.filter(
        (l) => l.labelId !== labelId
      ),
    })),

  setTaskLabels: (taskId, labels) =>
    set((state) => {
      const newTaskLabels = new Map(state.taskLabels);
      newTaskLabels.set(taskId, labels);
      return { taskLabels: newTaskLabels };
    }),

  setMultipleTaskLabels: (taskLabels) =>
    set((state) => {
      const newTaskLabels = new Map(state.taskLabels);
      taskLabels.forEach(({ taskId, labels }) => {
        newTaskLabels.set(taskId, labels);
      });
      return { taskLabels: newTaskLabels };
    }),

  addLabelToTask: (taskId, label) =>
    set((state) => {
      const newTaskLabels = new Map(state.taskLabels);
      const currentLabels = newTaskLabels.get(taskId) || [];
      if (!currentLabels.find((l) => l.labelId === label.labelId)) {
        newTaskLabels.set(taskId, [...currentLabels, label]);
      }
      return { taskLabels: newTaskLabels };
    }),

  removeLabelFromTask: (taskId, labelId) =>
    set((state) => {
      const newTaskLabels = new Map(state.taskLabels);
      const currentLabels = newTaskLabels.get(taskId) || [];
      newTaskLabels.set(
        taskId,
        currentLabels.filter((l) => l.labelId !== labelId)
      );
      return { taskLabels: newTaskLabels };
    }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearLabels: () =>
    set({
      labels: [],
      labelsWithCounts: [],
      taskLabels: new Map(),
      error: null,
    }),
}));

// Selectors
export const useLabels = () => useLabelStore((state) => state.labels);

export const useLabelsWithCounts = () =>
  useLabelStore((state) => state.labelsWithCounts);

export const useTaskLabels = (taskId: string) =>
  useLabelStore((state) => state.taskLabels.get(taskId) || []);

export const useLabelLoading = () => useLabelStore((state) => state.isLoading);

export const useLabelError = () => useLabelStore((state) => state.error);

export const useLabelById = (labelId: string) =>
  useLabelStore((state) => state.labels.find((l) => l.labelId === labelId));

export const useLabelsByColor = (color: string) =>
  useLabelStore((state) => state.labels.filter((l) => l.color === color));

export default useLabelStore;
